import { addDays } from 'date-fns';
import { createSelector } from '@reduxjs/toolkit';
import { fetchDisputedWonItems, fetchWonItems } from '@/redux/api/wonItems';
import { getAuthToken, getBidderId } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import { LOG_OUT_BIDDER } from './actions';
import { safeFromUnixTime } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { WonCatalog } from '@/types/WonCatalog';
import caseInsensitiveCompare from '@liveauctioneers/caterwaul-components/lib/utils/caseInsensitiveCompare';
import cloneDeep from 'lodash/cloneDeep';
import ms from 'ms';

/* Action Types */
export const LOAD_WON_ITEMS_FAIL = 'la/ui/wonItems/LOAD_FAIL';
export const LOAD_WON_ITEMS_REQUEST = 'la/ui/wonItems/LOAD_REQUEST';
export const LOAD_WON_ITEMS_SUCCESS = 'la/ui/wonItems/LOAD_SUCCESS';
export const CHANGE_WON_ITEMS_PAGE = 'la/ui/wonItems/CHANGE_WON_ITEMS_PAGE';
export const CHANGE_WON_ITEMS_PAGE_SIZE = 'la/ui/wonItems/CHANGE_WON_ITEMS_PAGE_SIZE';

const REDUX_STORE_TIME = ms('30m');

export const PAYMENT_STATUS_ASSUME_PAID = 'assumePaid';
export const PAYMENT_STATUS_DUE = 'due';
export const PAYMENT_STATUS_INVOICE_PENDING = 'invoicePending';
export const PAYMENT_STATUS_OVERDUE = 'overdue';
export const PAYMENT_STATUS_PAID = 'paid';
export const PAYMENT_STATUS_RESOLVED = 'resolved';

// if the sale started more than 51 days ago assume the bidder paid
const ASSUME_PAID_DAYS = 51;

const shouldAssumePaid = (saleStart: Date) => {
    const assumePaidDate = addDays(saleStart, ASSUME_PAID_DAYS);
    return safeFromUnixTime() > assumePaidDate;
};

const getPaymentStatus = (catalog: WonCatalog) => {
    const assumePaid = shouldAssumePaid(safeFromUnixTime(catalog.saleStartTs));
    const { hammerPrice, openDispute, paidStatus, sent } = catalog.invoice;

    let paymentStatus: string;
    if (catalog.invoice && !catalog.invoice.sent && !assumePaid) {
        paymentStatus = PAYMENT_STATUS_INVOICE_PENDING;
    } else if (!hammerPrice && sent) {
        paymentStatus = PAYMENT_STATUS_PAID;
    } else if (!openDispute && caseInsensitiveCompare(paidStatus, PAYMENT_STATUS_OVERDUE)) {
        paymentStatus = PAYMENT_STATUS_RESOLVED;
    } else if (assumePaid && !openDispute && !caseInsensitiveCompare(paidStatus, PAYMENT_STATUS_PAID)) {
        paymentStatus = PAYMENT_STATUS_ASSUME_PAID;
    } else if (openDispute) {
        paymentStatus = PAYMENT_STATUS_OVERDUE;
    } else {
        paymentStatus = paidStatus.toLowerCase();
    }
    return paymentStatus;
};

/* reducer */
export const DEFAULT_STATE = {
    items: [],
    loaded: null,
    loading: false,
    page: 1,
    pageSize: 10,
    totalItems: 0,
};

export type State = typeof DEFAULT_STATE & {
    loaded: number | null | undefined;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let wonItems: any[];
    switch (action.type) {
        case LOAD_WON_ITEMS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_WON_ITEMS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_WON_ITEMS_SUCCESS:
            wonItems = cloneDeep(action.payload.catalogs).map((wonCatalog: WonCatalog) => {
                wonCatalog.paymentStatus = getPaymentStatus(wonCatalog);
                wonCatalog.assumePaid = shouldAssumePaid(safeFromUnixTime(wonCatalog.saleStartTs));
                return wonCatalog;
            });
            return {
                ...state,
                items: wonItems,
                loaded: action.meta.actionTime,
                loading: false,
                totalItems: action.payload.count,
            };
        case CHANGE_WON_ITEMS_PAGE_SIZE:
            return {
                ...state,
                pageSize: action.payload,
            };
        case CHANGE_WON_ITEMS_PAGE:
            return {
                ...state,
                page: action.payload,
            };
        case LOG_OUT_BIDDER:
            return {
                ...DEFAULT_STATE,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.wonItems;

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);
const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getTotalWonItemsCount = createSelector(stateSelector, (state) => state.totalItems || 0);
export const getWonItems = createSelector(stateSelector, (state) => state.items || []);
export const getWonItemsPage: (arg0: any) => number = createSelector(stateSelector, (state) => state.page || 1);
export const getWonItemsPageSize: (arg0: any) => number = createSelector(
    stateSelector,
    (state) => state.pageSize || 10
);
export const areWonItemsLoaded = loadedSelector;
export const areWonItemsLoading = loadingSelector;

const shouldFetchWonItems = createSelector([loadedSelector, loadingSelector], (loaded, loading) => {
    if (loaded) {
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    return !loading;
});

/* ACTION CREATORS */
type FetchWonItemsIfNeededParams = {
    force?: boolean;
    page?: number;
    pageSize?: number;
};

export const fetchWonItemsIfNeeded =
    ({ force = false, page, pageSize }: FetchWonItemsIfNeededParams) =>
    async (dispatch: Function, getState: Function) => {
        const state = getState();

        if (shouldFetchWonItems(state) || force) {
            page = page || getWonItemsPage(state);
            pageSize = pageSize || getWonItemsPageSize(state);
            await changeWonItemsPage({ force: true, page, pageSize })(dispatch, getState);
        } else {
            return Promise.resolve();
        }
    };

export const fetchDisputedWonItemsIfNeeded =
    ({ page, pageSize }: FetchWonItemsIfNeededParams) =>
    async (dispatch: Function, getState: Function) => {
        const state = getState();

        page = page || getWonItemsPage(state);
        pageSize = pageSize || getWonItemsPageSize(state);
        await changeWonItemsPage({ force: true, isDisputed: true, page, pageSize })(dispatch, getState);
    };

type ChangeWonItemsPageParams = {
    force?: boolean;
    isDisputed?: boolean;
    page: number;
    pageSize: number;
};
export const changeWonItemsPage =
    ({ force, isDisputed, page, pageSize }: ChangeWonItemsPageParams) =>
    async (dispatch: Function, getState: Function) => {
        const state = getState();
        if (areWonItemsLoading(state)) {
            return;
        }
        const oldPage = getWonItemsPage(state);
        const oldPageSize = getWonItemsPageSize(state);
        let dirty = false;

        if (oldPage !== page && Boolean(page)) {
            dirty = true;
            dispatch({
                payload: Number(page),
                type: CHANGE_WON_ITEMS_PAGE,
            });
        }

        if (oldPageSize !== pageSize && Boolean(pageSize)) {
            dirty = true;
            dispatch({
                payload: Number(pageSize),
                type: CHANGE_WON_ITEMS_PAGE_SIZE,
            });
        }

        if (dirty || force) {
            try {
                const authToken = getAuthToken(state);
                const deployment = getDeployment(state);
                const bidderId = getBidderId(state);

                dispatch({
                    meta: { actionTime: Date.now() },
                    type: LOAD_WON_ITEMS_REQUEST,
                });
                let response;
                if (isDisputed) {
                    response = await fetchDisputedWonItems({
                        authToken,
                        bidderId,
                        deployment,
                        page,
                        pageSize: pageSize || oldPageSize,
                    });
                } else {
                    response = await fetchWonItems({
                        authToken,
                        bidderId,
                        deployment,
                        page,
                        pageSize: pageSize || oldPageSize,
                    });
                }
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: response.data,
                    type: LOAD_WON_ITEMS_SUCCESS,
                });
            } catch (error) {
                dispatch({
                    error: true,
                    payload: error,
                    type: LOAD_WON_ITEMS_FAIL,
                });
            }
        }
    };
